<template>
  <div class="schedule-view" role="region" :aria-label="pageName" data-test="schedule-view" :style="rootStyle">
    <!--
      The Super Admin's banner (Schedule - Chart Audit, 2026-10-28). Super Admins keep the page on ANY
      office; when the office does not qualify (the Teamio app is older than 3.0.61, or the switch is
      off in SuperAdmin) the service refuses the data, and this says why — naming the version — over the
      empty state below. Built from the service's own 403 (store accessRefusal), never from a second
      copy of the rule, and drawn for Super Admins only: an office user is told nothing but "not
      available" (they have no way to fix it).
    -->
    <p
      v-if="accessNotice"
      class="sv-access-banner"
      role="status"
      data-test="sv-access-banner"
    >{{ accessNotice }}</p>

    <!--
      No tab row (Nilay, 2026-10-23): the "Schedule / Tasks & Follow-ups" tabs are gone — the second
      was a disabled "Coming soon" since 9/19 and only pushed the page down. The page starts at the
      date row. Tasks & Follow-ups returns as its own page when it exists, not as a tab here.
    -->
    <!--
      TWO toolbar rows, like the design (`.tb-date` / `.tb-filters`): the date row here — ‹ date ›,
      the day in words, Today (when not on today, never on a phone), the freshness indicator and
      Refresh — and the filter row below. The freshness indicator rides in the toolbar's slot: it
      used to end the filter row, and moved up beside Refresh with the redesign.
    -->
    <ScheduleToolbar
      v-if="date && !accessRefusal"
      :date="date"
      :today="today"
      :busy="isRefreshing"
      :compact="isPhone"
      @change-date="goToDate"
      @refresh="refresh"
    >
      <!--
        The freshness indicator.

        ⛔ This is not decoration. Every other surface in Teamio is read once, on purpose, by
        somebody who knows they are reading history. This one is left open on a wall and believed.
        If the office sync stops, the page keeps drawing a perfectly plausible morning that stopped
        being true hours ago — and nobody would notice. A schedule that admits it is behind is far
        better than one that is quietly wrong.

        Healthy sites see a quiet "Live" with no number: the Open Dental heartbeat only arrives
        every ~10 minutes, so a permanent "last synced 8 minutes ago" would read as a warning on a
        page that is working perfectly, and staff would learn to ignore the one line that matters.

        On a phone the healthy "Live" is not drawn (the design drops it to save the row) — but ONLY
        the healthy one: "Last synced 15 min ago" and "Sync status unknown" still show everywhere,
        because they are the states that must never be hidden.
      -->
      <span
        class="sv-sync"
        :class="`sv-sync--${syncStatus.tone}`"
        :title="syncStatus.detail"
        data-test="sv-sync-status"
      >
        <span class="sv-sync__dot" aria-hidden="true"></span>
        {{ syncStatus.label }}
      </span>
    </ScheduleToolbar>

    <div v-if="day && !accessRefusal" class="sv-controls" data-test="sv-controls">
      <ScheduleFilterDropdown
        name="provider"
        label="Provider"
        :options="options.providers"
        :selected="filters.providers"
        @change="onFilterChange('providers', $event)"
      />
      <ScheduleFilterDropdown
        name="operatory"
        label="Operatory"
        :options="options.operatories"
        :selected="filters.operatories"
        @change="onFilterChange('operatories', $event)"
      />
      <ScheduleFilterDropdown
        name="status"
        label="Status"
        :options="facetRows.statuses"
        :selected="filters.statuses"
        @change="onFilterChange('statuses', $event)"
      />
      <!--
        Alerts (WP-A5, 2026-10-23) — replaced the strip of per-alert count cards. One row per alert the
        server actually RAN (not per alert the office switched on: a rule that was not computed has no
        row, never a confident "0"), icon · label · count, with the money under balance / claims /
        unscheduled treatment. Hidden until /alerts has answered and at least one rule ran.
      -->
      <ScheduleFilterDropdown
        v-if="facetRows.alerts.length"
        name="alert"
        label="Alerts"
        :options="facetRows.alerts"
        :selected="filters.alerts"
        @change="onFilterChange('alerts', $event)"
      />

      <!-- The gear sits next to Status, per the design. -->
      <ScheduleSettingsPopover
        ref="settings"
        :settings="viewSettings"
        :capabilities="capabilities"
        :reasons="capabilityReasons"
        :confirmation-statuses="day.confirmationStatuses || []"
        :saving="savingSettings"
        :error-message="settingsError"
        @save="onSaveSettings"
        @dismiss="settingsError = ''"
      />

      <!--
        The view switch, on EVERY width (WP-B, 2026-10-23): Schedule · Table view right after the gear
        on an iPad / desktop — where the design puts it — and Timeline · Columns · Table as a
        full-width pill in a band of its own under the filters on a phone. Which tabs, and which one
        is on, are decided HERE, in JS (tableView.js viewTabs / effectiveView — like the views below),
        not by hiding anything with CSS, so the switch and the view cannot disagree.
      -->
      <ScheduleViewSwitch
        :view="effectiveView"
        :tabs="viewTabs"
        :phone="isPhone"
        @change="onChangeView"
      />

      <button
        v-if="filtersActive"
        type="button"
        class="sv-controls__clear"
        data-test="sv-clear-filters"
        @click="clearFilters"
      >Clear filters</button>
    </div>

    <div v-if="isLoading" class="sv-state" data-test="sv-loading">Loading the schedule…</div>

    <div v-else-if="errorMessage" class="sv-state sv-state--error" data-test="sv-error">
      <p>{{ errorMessage }}</p>
      <button type="button" class="sv-state__retry" @click="refresh">Try again</button>
    </div>

    <!--
      The service refused this OFFICE (403 `unavailable` — switch off, or the Teamio app too old: a stale
      tab, or a Super Admin on an office that does not qualify). A plain empty state, not an error line
      or a toast: nothing is broken, the office simply does not have this page. "Check again" re-asks,
      so a tab left open while a Super Admin fixed the office recovers without a reload.
    -->
    <div v-else-if="accessRefusal" class="sv-state" data-test="sv-access-unavailable">
      <p>{{ pageName }} isn't available for this office right now.</p>
      <button type="button" class="sv-state__retry" data-test="sv-access-recheck" @click="recheckAccess">Check again</button>
    </div>

    <div v-else-if="unavailable" class="sv-state" data-test="sv-unavailable">
      No Open Dental schedule data has been loaded for this practice yet.
    </div>

    <div v-else-if="!hasOperatories" class="sv-state" data-test="sv-empty">
      No operatories are set up in Open Dental for this practice, so there is nothing to lay the
      day out in.
    </div>

    <template v-else>
      <!--
        A rule that FAILED is not a clean day: the Alerts list only has rows for rules that ran, so a
        failed one is simply absent from it — and "no balance row" reads as "nobody owes anything".
        This line is what says otherwise (it was the summary strip's, and it outlived the strip).
      -->
      <p
        v-if="alertErrorSentence"
        class="sv-hidden sv-hidden--error"
        data-test="sv-alert-errors"
      >{{ alertErrorSentence }}</p>

      <!--
        A filtered schedule looks exactly like a quiet day. Saying what is hidden is the difference
        between "nobody is coming this afternoon" and "you narrowed this to one hygienist".
      -->
      <p v-if="hiddenNotice" class="sv-hidden" data-test="sv-hidden-notice">{{ hiddenNotice }}</p>

      <div
        v-if="!filteredDay.operatories.length"
        class="sv-state"
        data-test="sv-no-match"
      >
        No operatories match the current filter.
        <button type="button" class="sv-state__retry" @click="clearFilters">Clear filters</button>
      </div>

      <!--
        The Table view (Phase 11): one row per appointment, every row carrying its visit checklist.
        It is handed the page's OWN `checklistByAptNum` — the one map the blocks, the chip and the
        card read — and the FILTERED day, so a row can never say more than its block does. Ticks go
        through the same store action as the card's. On a phone it is the third tab of the phone
        switch, and scrolls sideways inside its own host (Print is still there).
      -->
      <ScheduleTableView
        v-else-if="effectiveView === 'table'"
        :appointments="filteredAppointments"
        :operatories="filteredDay.operatories"
        :unfiltered-day="day"
        :providers="day.providers || []"
        :checklists="checklistByAptNum"
        :checks="checks"
        :bucket-map="bucketMap"
        :alert-payload="alertPayload"
        :sort="tableSort"
        :date="date || ''"
        :timezone="timezone || ''"
        :date-format="panelDateFormat"
        :practice-name="practiceName"
        :filters-active="filtersActive"
        @select-appointment="onSelectAppointment"
        @toggle="onToggleCheck"
        @change-sort="onChangeSort"
        @clear-filters="clearFilters"
      />

      <!--
        What is drawn is `effectiveView` (tableView.js): a phone's own choice on a phone — Timeline
        (one column of cards, Phase 6), Columns (the real chair grid, scrolled sideways) or Table —
        and the iPad's / desktop's choice everywhere else (the grid, or the table above). The
        timeline and the grid take the same props, so the page does not care which is on screen.
        Decided in JS rather than by hiding one with CSS: rendering both would double every block,
        card and alert icon on the page.
      -->
      <ScheduleTimeline
        v-else-if="effectiveView === 'timeline'"
        :day="filteredDay"
        :unfiltered-day="day"
        :view-settings="viewSettings"
        :bucket-map="bucketMap"
        :alert-payload="alertPayload"
        :now-minutes="nowMinutes"
        :is-today="date === today"
        :checklists="checklistByAptNum"
        :checklist-mode="checklistMode"
        @select-appointment="onSelectAppointment"
        @card-enter="onCardEnter"
        @card-leave="onCardLeave"
        @alert-focus="onAlertFocus"
        @open-checklist="onOpenChecklist"
      />

      <!-- 'schedule' (iPad / desktop) and 'columns' (a phone) are the same grid; `phone` narrows its columns. -->
      <ScheduleGrid
        v-else
        :phone="isPhone"
        :day="filteredDay"
        :unfiltered-day="day"
        :view-settings="viewSettings"
        :bucket-map="bucketMap"
        :alert-payload="alertPayload"
        :now-minutes="nowMinutes"
        :is-today="date === today"
        :checklists="checklistByAptNum"
        :checklist-mode="checklistMode"
        @select-appointment="onSelectAppointment"
        @card-enter="onCardEnter"
        @card-leave="onCardLeave"
        @alert-focus="onAlertFocus"
        @open-checklist="onOpenChecklist"
      />
    </template>

    <!--
      THE visit checklist's one layer (Phase 10e). Exactly one on the page: it holds which card is
      open, and every block and phone card only reports the pointer to it. It is always mounted —
      it draws nothing until a card opens — because it also owns the hover/touch detection that
      tells the blocks whether to draw a chip, and reports it through `mode`.
    -->
    <ChecklistLayer
      ref="checklistLayer"
      :appointments="filteredAppointments"
      :unfiltered-day="day"
      :bucket-map="bucketMap"
      :checklists="checklistByAptNum"
      :alert-payload="alertPayload"
      :date="date || ''"
      @mode="checklistMode = $event"
      @open-tab="onChecklistOpenTab"
    />

    <!--
      The Patient Panel (Phase 7). Everything it is given is LIVE — the appointment is looked up by
      AptNum in the current day on every render, and the alerts are the same payload the blocks
      read — so a patient who arrives while the panel is open changes status in the panel too.
      Which patient is open lives in the patientPanel store, so this host can later move to
      Layout.vue for the header patient search without the panel changing.
    -->
    <PatientPanel
      :appointment="panelAppointment"
      :appointment-meta="panelAppointmentMeta"
      :alert-payload="alertPayload"
      :view-settings="viewSettings"
      :timezone="timezone || ''"
      :date-format="panelDateFormat"
    />

    <!--
      This page's ONE vue2-slideout-panel outlet — the panel's Send Form / Text / Email and Create Task
      open through it ($showPanel). Exactly one: every mounted outlet renders every panel, so a second
      one draws a twin (ChatContainer.vue explains the Practice Pulse incident). The wrapper lifts the
      slide-outs (z-index ~101 of their own) above the Patient Panel (1035), which opened them.
    -->
    <div class="sv-slideout-host" data-test="sv-slideout-host">
      <slideout-panel></slideout-panel>
    </div>
  </div>
</template>

<script>
/**
 * Schedule View — the office's day, laid out by operatory the way Open Dental draws it.
 *
 * Plan: docs/plans/2026-09-19-schedule-view-plan.md
 * Rules ledger: teamio-serverless-api/schedule-view-service/docs/schedule-view.md
 *
 * The grid (or the table) on an iPad or desktop; on a phone the timeline, the grid scrolled sideways
 * ("Columns") or the table, chosen with the switch under the filters (Phase 6, WP-B 2026-10-23). All
 * with alert icons, the settings popover and live refresh. Clicking a block or a card opens the
 * Patient Panel (Phase 7, src/components/PatientPanel/).
 *
 * ⛔ DO NOT declare props named `patNum`, `siteID` or `loading` on this page or any Schedule View
 * component: mixin.js defines computeds with those names and a computed SILENTLY REPLACES a
 * same-named prop. Guarded by cypress/node-tests/mixin-prop-shadow-guardrail.test.js.
 */
import { mapState, mapGetters } from 'vuex';
import ScheduleToolbar from './components/ScheduleToolbar.vue';
import ScheduleGrid from './components/ScheduleGrid.vue';
import ScheduleTimeline from './components/ScheduleTimeline.vue';
import ScheduleFilterDropdown from './components/ScheduleFilterDropdown.vue';
import ScheduleSettingsPopover from './components/ScheduleSettingsPopover.vue';
import ScheduleViewSwitch from './components/ScheduleViewSwitch.vue';
import ScheduleTableView from './components/ScheduleTableView.vue';
import ChecklistLayer from './components/ChecklistLayer.vue';
import { todayForSite, nowMinutesForSite } from '@/store/scheduleView';
import { buildBucketMap, resolveBucket, statusLabel, isBroken } from '@/utils/scheduleView/statusBuckets';
import PatientPanel from '@/components/PatientPanel/PatientPanel.vue';
import { dateFormatFor } from '@/utils/taskDraft';
import {
  applyFilters, filterOptions, hasActiveFilters, facetCounts, facetOptions,
} from '@/utils/scheduleView/filters';
import {
  summaryCounts, alertTotal, formatMoney, failedAlertsSentence, visibleAlertKeys, blockAlertKeys,
  alertsForPatient, describeAlert,
} from '@/utils/scheduleView/alerts';
import { checklistsForDay } from '@/utils/scheduleView/checklist';
import { ALERT_KEYS, ALERT_META } from '@/utils/scheduleView/settingsForm';
import { describeSync, isSignalHealthy, nextPulseDelay } from '@/utils/scheduleView/liveness';
import { SCHEDULE_PAGE_NAME, describeAccessNotice } from '@/utils/scheduleView/access';
import { PHONE_MEDIA_QUERY, appointmentProviderNum } from '@/utils/scheduleView/layout';
import { effectiveView as pickEffectiveView, viewTabs as tabsForDevice } from '@/utils/scheduleView/tableView';

/** How often the NOW line moves. A minute is as precise as a 76px/hour grid can show. */
const NOW_TICK_MS = 60000;
/**
 * Alerts refresh more slowly than the grid, deliberately.
 *
 * The schedule changes minute to minute — people are confirmed, arrive, get seated. The things
 * alerts measure do not: a family balance, an aged claim and an overdue recall are the same at
 * 10:02 as at 10:00. These are five queries against procedurelog and claim, which are the
 * expensive tables on a mature practice, so running them at the grid's cadence would multiply that
 * cost for no new information.
 */
const ALERT_REFRESH_MS = 600000;

export default {
  name: 'ScheduleView',

  components: {
    ScheduleToolbar, ScheduleGrid, ScheduleTimeline, ScheduleFilterDropdown, ScheduleSettingsPopover,
    ScheduleViewSwitch, ScheduleTableView, PatientPanel, ChecklistLayer,
  },

  data() {
    return {
      /**
       * How far the page pulls itself UP into the app layout's own top padding (px): the layout's
       * `.content` pads every route 20–40px from the top bar, and a page that wants its first row
       * close to the bar has to offset that from its OWN root. Measured, not hard-coded — see
       * `measurePull`.
       */
      pullUp: 0,
      nowMinutes: 0,
      isRefreshing: false,
      pulseTimer: null,
      nowTimer: null,
      /** A failed settings save's reason, shown inside the popover rather than as a toast. */
      settingsError: '',
      /** When the alerts were last fetched, so they can refresh on their own slower clock. */
      lastAlertFetch: 0,
      /**
       * True until the first load has settled. The very first fetch has to guess "today" from the
       * browser, because only the response carries the practice's timezone; this flag lets that
       * one load correct itself without re-fetching on every later navigation.
       */
      wasDefaultLoad: true,
      /**
       * True on a phone-width window: the phone's own views (timeline · columns · table) and its own
       * switch, instead of the iPad / desktop's. See PHONE_MAX_WIDTH.
       */
      isPhone: false,
      /** The live media-query listener, kept so it can be removed. */
      viewportWatch: null,
      /**
       * How the visit checklist opens: 'hover' (a mouse) | 'pop' | 'sheet' (touch). Decided by the
       * ChecklistLayer, which owns the media queries, and reported up so the blocks know whether to
       * draw a chip. 'hover' until it reports, which draws no chip — a mouse is the common case.
       */
      checklistMode: 'hover',
    };
  },

  computed: {
    ...mapState('scheduleView', {
      date: state => state.date,
      day: state => state.day,
      isLoading: state => state.loading,
      errorMessage: state => state.error,
      unavailable: state => state.unavailable,
      accessRefusal: state => state.accessRefusal,
      signalListening: state => state.signalListening,
      filters: state => state.filters,
      savingSettings: state => state.savingSettings,
      checks: state => state.checks,
      view: state => state.view,
      phoneView: state => state.phoneView,
      tableSort: state => state.tableSort,
    }),

    ...mapGetters('scheduleView', ['capabilities', 'capabilityReasons', 'alertsForDate', 'sync', 'taskLinesByPatNum']),

    /** The page's name: the nav entry, the top bar and the Super Admin card say the same words. */
    pageName() {
      return SCHEDULE_PAGE_NAME;
    },

    /**
     * The Super Admin's banner sentence ('' for everyone else, and while the office has access).
     * Read off the service's refusal — which names the version it saw — so the page never carries a
     * second copy of the rule that could disagree with the data route.
     */
    accessNotice() {
      const customer = this.$store.state.customer;
      const user = customer && customer.user;
      if (!(user && user.Role === 'Super Admin')) return '';
      return describeAccessNotice(this.accessRefusal);
    },

    /**
     * How long to wait before the next pulse (liveness.js): 5 minutes while the live signal is
     * healthy — the listener is delivering AND the freshness indicator is "Live", the two notions of
     * health this page already has — and 30 seconds when it is not.
     */
    pulseDelay() {
      return nextPulseDelay(true, isSignalHealthy({
        listening: this.signalListening,
        syncState: this.sync && this.sync.state,
      }));
    },

    /** Alerts, but only ever the ones belonging to the day on screen. See the store's getter. */
    alertPayload() {
      return this.alertsForDate;
    },

    /**
     * Every patient on the day, for the live `task` listeners.
     *
     * ⛔ Deduped. A patient booked twice would otherwise occupy two of the thirty slots in an
     * `array-contains-any` query, and on a busy day that is how a practice quietly ends up needing
     * a fourth listener for patients it is already watching.
     *
     * Taken from the UNFILTERED day: the Provider/Operatory/Status filters narrow what is DRAWN,
     * and re-subscribing every time somebody flicks a filter would tear down and rebuild every
     * listener for no gain.
     */
    dayPatNums() {
      const appts = (this.day && this.day.appointments) || [];
      const seen = [];
      appts.forEach((appt) => {
        const p = appt && appt.PatNum;
        if (p === null || p === undefined) return;
        const key = String(p);
        if (seen.indexOf(key) === -1) seen.push(key);
      });
      return seen;
    },

    /** The freshness indicator's words. Pure, so the wording is testable without mounting. */
    syncStatus() {
      return describeSync(this.sync);
    },

    /** The version the last pulse reported, used to re-baseline after a manual refresh. */
    pulseVersion() {
      const pulse = this.$store.state.scheduleView.pulse;
      return (pulse && pulse.date === this.date) ? pulse.v : null;
    },

    /** The one custom property the stylesheet reads (`--sv-pull`); nothing at all until it is measured. */
    rootStyle() {
      return this.pullUp > 0 ? { '--sv-pull': `${this.pullUp}px` } : null;
    },

    /**
     * What is actually drawn: a phone's own remembered choice on a phone ('timeline' | 'columns' |
     * 'table'), the iPad's / desktop's everywhere else ('schedule' | 'table'). A phone and an iPad
     * keep SEPARATE choices (tableView.js), so neither one's switch rewrites the other's.
     */
    effectiveView() {
      return pickEffectiveView(this.isPhone, { view: this.view, phoneView: this.phoneView });
    },

    /** The switch's tabs for this device: Schedule · Table view, or Timeline · Columns · Table. */
    viewTabs() {
      return tabsForDevice(this.isPhone);
    },

    /** The site's timezone, once the first payload has arrived. */
    timezone() {
      return this.day ? this.day.timezone : null;
    },

    /**
     * The practice's name, for the printed sheet's kicker; '' when the settings are not loaded.
     *
     * Read off the store's settings rows ({ Property, PropertyValue }) directly. NOT through the
     * global mixin's `allSettings`: this page does not include that mixin (it is opt-in per
     * component, `mixins: [mixin]`), and `allSettings` also needs a bare global `_`.
     */
    practiceName() {
      const rows = this.$store.state.customer && this.$store.state.customer.settings;
      if (!Array.isArray(rows)) return '';
      const row = rows.find(r => r && r.Property === 'GENERAL_PRACTICE_NAME');
      return row && row.PropertyValue ? String(row.PropertyValue).trim() : '';
    },

    today() {
      return todayForSite(this.timezone);
    },

    /**
     * Settings always come from the payload the server resolved — the page never keeps its own
     * copy of the defaults, or the two would drift and the office would be told one thing while
     * seeing another.
     */
    viewSettings() {
      return this.day && this.day.settings ? this.day.settings : null;
    },

    bucketMap() {
      if (!this.day) return {};
      return buildBucketMap(
        this.day.confirmationStatuses,
        this.viewSettings ? this.viewSettings.confirmationBuckets : {}
      );
    },

    hasOperatories() {
      return !!(this.day && this.day.operatories && this.day.operatories.length);
    },

    options() {
      return filterOptions(this.day);
    },

    filtersActive() {
      return hasActiveFilters(this.filters);
    },

    /** The day the grid actually draws. Identical to `day` when nothing is filtered. */
    filtered() {
      return applyFilters(
        this.day,
        this.filters,
        appt => resolveBucket(appt, this.bucketMap),
        appt => visibleAlertKeys(this.alertPayload, appt.PatNum, ALERT_KEYS)
      );
    },

    filteredDay() {
      return this.filtered.day;
    },

    filteredAppointments() {
      return (this.filteredDay && this.filteredDay.appointments) || [];
    },

    /**
     * Every visit checklist for the day on screen, built ONCE per render and handed to the blocks
     * (the chip), the phone cards and the layer (the card) — so the icon row, the chip and the card
     * can never give three answers.
     *
     * ⛔ Built from `blockAlertKeys`, the same function the blocks call for their icons, over the
     * FILTERED day (what is actually drawn). A checklist built from the raw alert payload would list
     * an alert the office switched off, or one whose rule failed, beside a block that shows no icon.
     * Re-derived whenever a tick lands (`checks`), so the chip and the progress bar move with it.
     */
    checklistByAptNum() {
      return checklistsForDay({
        appointments: this.filteredAppointments,
        payload: this.alertPayload,
        viewSettings: this.viewSettings,
        order: ALERT_KEYS,
        tasksByPatNum: this.taskLinesByPatNum,
        checks: this.checks,
        today: this.today,
        deps: { blockAlertKeys, alertsForPatient, describe: describeAlert },
      });
    },

    /**
     * The numbers in the Status and Alerts dropdowns (WP-A5, 2026-10-23).
     *
     * ⛔ Counted on the UNFILTERED day narrowed by Provider + Operatory ONLY — the statuses and
     * alerts selections are ignored (utils/scheduleView/filters.js facetCounts). The old strip counted
     * the fully filtered day, so ticking "Balance" turned every other alert's count into 0 and the
     * rows stopped saying what ticking them would show. Each row is now "how many appointments this
     * would add", whatever else is ticked.
     */
    facets() {
      return facetCounts(
        this.day ? this.day.appointments : [],
        this.filters,
        this.alertPayload,
        appt => resolveBucket(appt, this.bucketMap),
        { summaryCounts, alertTotal }
      );
    },

    /** The Status and Alerts dropdown rows: counts, money, and the alerts' own icons and words. */
    facetRows() {
      return facetOptions(this.options.statuses, this.facets, ALERT_META, formatMoney);
    },

    /** Names the alert rules that could not be checked today ('' when all ran). */
    alertErrorSentence() {
      return failedAlertsSentence(
        this.alertPayload && this.alertPayload.errors,
        key => (ALERT_META.find(meta => meta.key === key) || {}).label
      );
    },

    /**
     * The appointment the Patient Panel was opened from, looked up LIVE in the unfiltered day.
     * Null when it is no longer on this day (moved by the office, or the user changed date) — the
     * panel then keeps the patient and drops only the "This appointment" card.
     */
    panelAppointment() {
      const aptNum = this.$store.state.patientPanel.openAptNum;
      if (!aptNum || !this.day) return null;
      return (this.day.appointments || []).find(a => a.AptNum === aptNum) || null;
    },

    /** Chair, provider and status for that appointment, resolved the way the block resolves them. */
    panelAppointmentMeta() {
      const appt = this.panelAppointment;
      if (!appt) return null;
      const op = (this.day.operatories || []).find(o => o.OperatoryNum === appt.Op);
      const provNum = appointmentProviderNum(appt);
      const prov = (this.day.providers || []).find(p => p.ProvNum === provNum);
      const bucket = resolveBucket(appt, this.bucketMap);
      return {
        opName: op ? (op.OpName || op.Abbrev || '') : '',
        providerName: prov ? `${prov.FName || ''} ${prov.LName || ''}`.trim() || prov.Abbr || '' : '',
        bucket,
        statusText: statusLabel(appt, bucket),
        broken: isBroken(appt),
      };
    },

    /** MM/DD/YYYY, or DD/MM/YYYY for an office that writes day first — Teamio's one rule. */
    panelDateFormat() {
      return dateFormatFor(this.timezone);
    },

    hiddenNotice() {
      const { hiddenAppointments, hiddenOperatories } = this.filtered;
      if (!hiddenAppointments && !hiddenOperatories) return '';
      const parts = [];
      if (hiddenAppointments) {
        parts.push(`${hiddenAppointments} appointment${hiddenAppointments === 1 ? '' : 's'}`);
      }
      if (hiddenOperatories) {
        parts.push(`${hiddenOperatories} operator${hiddenOperatories === 1 ? 'y' : 'ies'}`);
      }
      return `${parts.join(' and ')} hidden by the current filter.`;
    },
  },

  created() {
    // Before the first paint, so a phone never draws the iPad's views for one frame and then swaps.
    this.watchViewport();

    // Per-browser narrowing, restored before the first paint so the grid does not visibly reflow
    // from "everything" to "what this person actually looks at".
    this.$store.dispatch('scheduleView/loadFilters');

    // The remembered views (schedule / table, and the phone's timeline / columns / table) and the
    // table's sort, the same way and for the same reason: restored before the first paint, so the
    // page does not flash the timeline and then swap.
    this.$store.dispatch('scheduleView/loadTablePrefs');

    // The day is resolved against the SITE's timezone, which only the payload knows. The first
    // load therefore asks for the browser's today and re-resolves once the timezone is known —
    // rather than blocking the first paint on a settings round trip.
    this.goToDate(todayForSite(null));
  },

  watch: {
    // The signal became healthy (the poll relaxes to 5 minutes) or stopped being healthy (back to 30
    // seconds). Restart the wait on the new clock now, rather than finishing a 5-minute wait that was
    // meant for a signal that is no longer there.
    pulseDelay() {
      this.scheduleLivePoll();
    },

    // The checklist layer's own rule: a card closes when the view changes. In the table no block
    // exists to call enter(), so nothing can open one — this only clears one left open by the grid.
    effectiveView() {
      if (this.$refs.checklistLayer) this.$refs.checklistLayer.close();
    },
  },

  mounted() {
    // Before the first paint (mounted runs in the same task that inserts the page), so the date row
    // never draws at the layout's padding and then jumps up.
    this.measurePull();
    window.addEventListener('resize', this.measurePull);

    this.tickNow();
    this.nowTimer = setInterval(this.tickNow, NOW_TICK_MS);
    // The liveness loop. This replaced a blind two-minute `/day` refetch: the office leaves this
    // page open on an operatory iPad and expects it to track Open Dental without anyone pressing
    // Refresh, and two minutes is long enough that staff would reach for the button first. Adaptive
    // (pulseDelay): every 30s until the live signal is healthy, then every 5 minutes behind it.
    this.scheduleLivePoll();
    document.addEventListener('visibilitychange', this.onVisibilityChange);

    // The office's own machine, telling us the moment a day changes (Phase 3Lb). Purely an
    // accelerator: it feeds the same refetch path as the timer, and an office without it — old
    // exe, setting off, firewalled Firestore — is slower, never broken. The poll above is what
    // guarantees correctness; this is what makes it feel instant.
    this.$store.dispatch('scheduleView/watchSignal');
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.measurePull);
    if (this.nowTimer) clearInterval(this.nowTimer);
    if (this.pulseTimer) clearTimeout(this.pulseTimer);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    this.unwatchViewport();
    this.$store.dispatch('scheduleView/unwatchSignal');
    this.$store.dispatch('scheduleView/unwatchTasks');
    // Leaving the page closes the panel and releases its Firestore listener. Left open, it would
    // reappear over whatever page the user comes back to, showing a patient they have moved on from.
    this.$store.dispatch('patientPanel/close');
  },

  methods: {
    async goToDate(date) {
      await this.$store.dispatch('scheduleView/fetchDay', { date });
      this.tickNow();

      // Refused (the office has no access — see the not-available state): there is no day to decorate
      // or follow, and alerts / pulse would only be refused again. recheckAccess comes back here.
      if (this.accessRefusal) return;

      // NOT awaited, on purpose. The grid is already drawable; the icons decorate it when they
      // arrive. Awaiting would make a slow claims query on a big practice the reason nobody can
      // see their schedule — which is precisely what splitting the two endpoints was for.
      this.lastAlertFetch = Date.now();
      this.$store.dispatch('scheduleView/fetchAlerts', { date });

      // The `task` alert is LIVE rather than fetched, so it is re-pointed at the new day's
      // patients here. Re-watching unsubscribes first, which matters on a date change: leaving
      // yesterday's listeners running would keep yesterday's patients' icons on screen and go on
      // paying for the reads.
      this.$store.dispatch('scheduleView/watchTasks', this.dayPatNums);

      // Prime the live loop NOW rather than letting the timer get to it, for two reasons found by
      // watching the real page on dev:
      //   1. the freshness indicator has nothing to report until the first pulse lands, so the
      //      page opened saying "Sync status unknown" for a full interval — an alarming thing to
      //      show somebody for thirty seconds on a schedule that is working perfectly;
      //   2. the first tick is spent ADOPTING the day's version, so the first real comparison did
      //      not happen until the SECOND tick — a change made just after load took ~60s to show,
      //      not the ~30s the interval implies.
      // Same single refetch path as everything else, and not awaited: the grid is already drawn.
      this.$store.dispatch('scheduleView/liveTick');

      // Now that the payload has told us the practice's timezone, check that the browser's idea
      // of "today" agreed with the practice's. It will not for a user in another timezone near
      // midnight, and silently showing them the wrong day is the failure this catches.
      //
      // ⛔ The flag is spent by the FIRST load, whether or not it needed correcting. It used to be
      // cleared only inside the correction, so a page whose first guess was right (the usual case)
      // kept it set, and the FIRST press of ‹ or › after opening the page was "corrected" straight
      // back to today — the arrow appeared to do nothing (found 2026-10-23 testing the date row).
      const firstLoad = this.wasDefaultLoad;
      this.wasDefaultLoad = false;
      const siteToday = todayForSite(this.timezone);
      if (date !== siteToday && firstLoad) {
        await this.$store.dispatch('scheduleView/fetchDay', { date: siteToday });
      }
    },

    /**
     * The manual Refresh button.
     *
     * Kept even though the page now follows Open Dental by itself, and deliberately made
     * UNCONDITIONAL: it refetches the day whatever the pulse says. Somebody pressing Refresh is
     * telling us they do not believe the screen, and answering that with "the version is
     * unchanged, so I did nothing" is how a page loses an office's trust for good — especially
     * given the one gap the pulse has (a blockout re-assigned between chairs leaves no stamp).
     */
    async refresh() {
      this.isRefreshing = true;
      try {
        await this.$store.dispatch('scheduleView/fetchDay', { date: this.date, silent: true });
        // Re-baseline, so the next pulse compares against what is now drawn rather than
        // immediately refetching the day this just loaded.
        await this.$store.dispatch('scheduleView/fetchPulse', { date: this.date });
        this.$store.commit('scheduleView/setDrawnVersion', this.pulseVersion);
      } finally {
        this.isRefreshing = false;
      }
      // Alerts follow the schedule but on their own clock (see ALERT_REFRESH_MS): the grid is
      // what changes minute to minute as people are confirmed and seated. A balance does not.
      if (Date.now() - this.lastAlertFetch >= ALERT_REFRESH_MS) this.refreshAlerts();
    },

    refreshAlerts() {
      this.lastAlertFetch = Date.now();
      this.$store.dispatch('scheduleView/fetchAlerts', { date: this.date });
    },

    /**
     * One tick of the liveness loop, but only while the tab is actually being looked at.
     *
     * Polling a hidden tab spends the practice's budget on a screen nobody is reading — an iPad
     * face-down on a counter, or a tab behind twelve others. Nothing is lost by pausing, because
     * becoming visible triggers an immediate check (below) rather than waiting for the next tick.
     *
     * The single-flight guard lives in the store action, not here, so that the timer, the focus
     * handler and the manual button cannot each invent their own version of it.
     */
    livePoll() {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      if (this.isLoading) return;
      // Refused: there is no day to follow, and a 403 every few seconds from a stale tab helps nobody.
      // "Check again" (recheckAccess) is how the page finds out the office qualifies now.
      if (this.accessRefusal) return;
      this.$store.dispatch('scheduleView/liveTick');
    },

    /**
     * Wait pulseDelay, tick once, wait again. A chain of timeouts rather than one setInterval because
     * the delay changes with the signal's health (see the `pulseDelay` watcher). A hidden tab still
     * ticks the timer but livePoll skips it, and becoming visible checks at once.
     */
    scheduleLivePoll() {
      if (this.pulseTimer) clearTimeout(this.pulseTimer);
      this.pulseTimer = setTimeout(() => {
        this.livePoll();
        this.scheduleLivePoll();
      }, this.pulseDelay);
    },

    /** "Check again" on the not-available state: the whole load again, which clears the refusal if it has lifted. */
    async recheckAccess() {
      await this.goToDate(this.date);
    },

    /**
     * Coming back to the tab checks IMMEDIATELY rather than waiting out the poll interval.
     *
     * This is the moment staleness is most likely and most visible: somebody has walked back to
     * the iPad to look something up, and whatever happened while the screen was off is exactly
     * what they are about to read.
     */
    onVisibilityChange() {
      if (document.visibilityState === 'visible') this.livePoll();
    },

    tickNow() {
      this.nowMinutes = nowMinutesForSite(this.timezone);
    },

    /**
     * Read the app layout's top padding off the element this page sits in (`.content`), so the page
     * can pull itself up by exactly that much (the stylesheet's `margin-top: calc(-1 * var(--sv-pull))`)
     * and put the date row 12px under the app's top bar on every width.
     *
     * ⛔ Measured rather than typed because the layout's number is not one number: `.content` is 40px
     * on a desktop, 20px between 768 and 1024px, 40px again on a phone, from three different media
     * queries in Layout.scss. A copy of those breakpoints here would drift the day somebody changes
     * the shared padding; reading it cannot. The shared Layout / AppShell are NOT touched — every other
     * page keeps the padding it relies on. A page not inside a padded host (a test, a print) pulls 0.
     */
    measurePull() {
      const host = this.$el && this.$el.parentElement;
      if (!host || typeof window.getComputedStyle !== 'function') return;
      const pad = parseFloat(window.getComputedStyle(host).paddingTop);
      const next = Number.isFinite(pad) && pad > 0 ? pad : 0;
      if (next !== this.pullUp) this.pullUp = next;
    },

    /**
     * Follow the window across the phone breakpoint — rotating a phone, or narrowing a desktop
     * window, swaps the iPad's views and switch for the phone's live. Nothing is refetched: they all
     * draw the same store state.
     */
    watchViewport() {
      if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
      const mql = window.matchMedia(PHONE_MEDIA_QUERY);
      const onChange = () => { this.isPhone = !!mql.matches; };
      // Safari < 14 — older operatory iPads — only has the deprecated addListener.
      if (mql.addEventListener) mql.addEventListener('change', onChange);
      else if (mql.addListener) mql.addListener(onChange);
      this.viewportWatch = { mql, onChange };
      onChange();
    },

    unwatchViewport() {
      const watch = this.viewportWatch;
      if (!watch) return;
      if (watch.mql.removeEventListener) watch.mql.removeEventListener('change', watch.onChange);
      else if (watch.mql.removeListener) watch.mql.removeListener(watch.onChange);
      this.viewportWatch = null;
    },

    onFilterChange(key, values) {
      this.$store.dispatch('scheduleView/updateFilters', { ...this.filters, [key]: values });
    },

    clearFilters() {
      this.$store.dispatch('scheduleView/updateFilters', {
        providers: [], operatories: [], statuses: [], alerts: [],
      });
    },

    async onSaveSettings(form) {
      this.settingsError = '';
      const result = await this.$store.dispatch('scheduleView/saveSettings', { form });
      if (result && result.ok) {
        if (this.$refs.settings) this.$refs.settings.close();
        this.$toast.success('Schedule View settings saved for this office.');
        return;
      }
      // Shown inside the popover, beside the form they can still correct — not as a toast that
      // appears after the panel has been torn down.
      this.settingsError = (result && result.errors && result.errors[0])
        || 'Could not save those settings. Please try again.';
    },

    /**
     * A block (or a phone card) was clicked: open that patient in the Patient Panel.
     *
     * The capabilities decide which optional sources the panel fetches at all — a site without
     * Insurance Verification or Tx Presenter gets no request for them, not a request that comes
     * back empty. "Today" is the SITE's today, which is what "upcoming appointments" is measured
     * from on Patient Profile too.
     *
     * `tab` (optional) lands the panel on that tab — the visit checklist's category arrows pass one.
     * A plain click passes none and opens exactly as before.
     */
    onSelectAppointment(appt, tab) {
      if (!appt || !appt.PatNum) return;
      const caps = this.capabilities || {};
      const request = {
        patNum: appt.PatNum,
        aptNum: appt.AptNum,
        today: this.today,
        capabilities: { insverify: !!caps.insverify, txsign: !!caps.txsign },
      };
      if (typeof tab === 'string' && tab) request.tab = tab;
      this.$store.dispatch('patientPanel/open', request);
    },

    // ── the visit checklist: blocks and cards report, the one layer decides ─────────────────────
    onCardEnter(e) {
      if (this.$refs.checklistLayer) this.$refs.checklistLayer.enter(e);
    },

    onCardLeave() {
      if (this.$refs.checklistLayer) this.$refs.checklistLayer.leave();
    },

    onAlertFocus(e) {
      if (this.$refs.checklistLayer) this.$refs.checklistLayer.focusAlert(e);
    },

    onOpenChecklist(e) {
      if (this.$refs.checklistLayer) this.$refs.checklistLayer.toggle(e);
    },

    /** A category arrow (or "Open patient profile") on the card: open the panel on that tab. */
    onChecklistOpenTab({ appt, tab } = {}) {
      this.onSelectAppointment(appt, tab);
    },

    // ── the view switch and the Table view ──────────────────────────────────────────────────────
    /** A phone's switch writes the phone's half of the remembered choice, an iPad's the other. */
    onChangeView(view) {
      this.$store.dispatch(this.isPhone ? 'scheduleView/updatePhoneView' : 'scheduleView/updateView', view);
    },

    onChangeSort(sort) {
      this.$store.dispatch('scheduleView/updateTableSort', sort);
    },

    /**
     * A to-do was ticked in the table: the SAME store action as the card's tick, so one tick flips
     * both. The store is free of UI, so a failed save is reported here (the tick has already been
     * rolled back) — in the same words as ChecklistLayer.onToggle.
     */
    async onToggleCheck({ aptNum, itemKey, checked } = {}) {
      if (aptNum === null || aptNum === undefined || !itemKey) return;
      const result = await this.$store.dispatch('scheduleView/toggleCheck', { aptNum, itemKey, checked: !!checked });
      if (result && result.ok === false && this.$toast && typeof this.$toast.error === 'function') {
        this.$toast.error(result.error || 'Could not save the checkmark.');
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import 'tokens';

.schedule-view {
  // Hanken Grotesk is already loaded app-wide (src/styles/theme.scss) and is what the newer Teamio
  // surfaces use — the design export's Plus Jakarta Sans is deliberately NOT added.
  font-family: 'Hanken Grotesk', 'Montserrat', sans-serif;
  // The date row sits 12px under the app's top bar: the layout's own top padding (20–40px, shared by
  // every route, so left alone) is cancelled from HERE — `measurePull` measures it into --sv-pull —
  // and 12px of this page's own padding is what is left. Print resets the pull (ScheduleTableView).
  margin-top: calc(-1 * var(--sv-pull, 0px));
  padding: 12px 16px 16px;
}

// A stacking context one above the Patient Panel (1035), so the slide-outs it opens — which draw at
// ~101 inside it — land on top of the panel instead of underneath. Below Bootstrap's modal layer
// (1040), so a modal a slide-out opens is still on top of the slide-out.
.sv-slideout-host {
  position: relative;
  z-index: 1036;
}

// The filter row (the design's `.tb-filters`): Provider · Operatory · Status · Alerts · gear · switch.
// A phone's switch is a full-width pill (`width: 100%`), so it wraps onto a row of its own — the band
// under the filters — and the Clear filters button, which is later in the markup, is ordered ahead of it.
.sv-controls {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 14px;
}

.sv-controls__clear {
  height: 38px;
  padding: 0 14px;
  border: 1px solid $sv-line;
  border-radius: $sv-radius;
  background: $sv-surface;
  box-shadow: $sv-shadow-1;
  color: $sv-brand;
  font: inherit;
  font-size: 13.5px;
  font-weight: 600;
  cursor: pointer;

  &:hover { background: $sv-bg; border-color: $sv-line-strong; }
}

/**
 * The freshness indicator (it sits beside Refresh, in the date row).
 *
 * Weighted to be read at a glance from across a room and IGNORED when healthy. "Live" is quiet —
 * teal text, a small dot with a soft ring, no border — because a badge shouting that everything is
 * fine trains people to stop looking at it. "Stale" gets the practice's warning colour and a filled
 * chip, because by then the number IS the message.
 */
.sv-sync {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: 38px;
  padding: 0 10px;
  border-radius: $sv-radius;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  cursor: default;

  &__dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: currentColor;
    flex: 0 0 auto;
  }

  &--live {
    color: $sv-brand;
    background: transparent;

    .sv-sync__dot { box-shadow: 0 0 0 3px $sv-brand-subtle; }
  }

  &--stale {
    color: #a5292e;
    background: #fdecec;
    // The one state that earns a border: it has to survive being glanced at from the far side of
    // an operatory, where the colour alone is not enough on a bright screen.
    border: 1px solid #f3c6c6;
  }

  &--unknown {
    color: $sv-ink-3;
    background: transparent;
  }
}

// The Super Admin's "this office does not qualify" banner: the FYI amber, over the empty state.
.sv-access-banner {
  margin: 0 0 12px;
  padding: 10px 14px;
  border-radius: $sv-radius;
  background: $sv-amber-soft;
  color: $sv-fyi-ink;
  font-size: 13.5px;
  font-weight: 600;
}

.sv-hidden {
  margin: 0 0 8px;
  font-size: 12px;
  font-weight: 600;
  color: $sv-ink-3;

  // A rule that could not be checked: the one line on this page that must not read as routine.
  &--error { color: #a5292e; }
}

.sv-state {
  border: 1px dashed $sv-line;
  border-radius: $sv-radius-lg;
  background: $sv-surface;
  padding: 24px;
  font-size: 14px;
  color: $sv-ink-3;
  max-width: 640px;

  &--error { border-style: solid; border-color: #e5484d; color: #a5292e; }
}

.sv-state__retry {
  margin-top: 8px;
  height: 32px;
  padding: 0 12px;
  border: 1px solid currentColor;
  border-radius: $sv-radius;
  background: transparent;
  color: inherit;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

// Phone: no side padding of our own — the app layout already leaves a 20px gutter, and with
// Teamio's sidebar taking 47px of a 390px screen, a second gutter comes straight off the cards.
@media (max-width: $sv-phone-max) {
  .schedule-view { padding: 12px 0 0; }

  // The design drops the healthy "Live" on a phone to save the row. ONLY the healthy one: a stale or
  // unknown sync state is the thing that must never be hidden, so those stay on every width.
  .sv-sync--live { display: none; }

  // A stale / unknown sync state shares the SECOND line of the date row with the day in words
  // (ScheduleToolbar's phone block orders: group 0, Refresh 1, line break 2, day 3, this chip 4), and is
  // sized to sit beside 13px text rather than beside a 38px button. It never shrinks or wraps (the day
  // beside it gives way), and it drops its dot: the chip's own words and fill carry the state, and the
  // dot's 14px is what keeps "Last synced 125 min ago" + the day + TODAY on ONE line in a ~303px page
  // (a classic-sidebar phone) instead of a third.
  .sv-sync {
    order: 4;
    flex: 0 0 auto;
    height: 28px;
    padding: 0 8px;
    font-size: 12.5px;
  }

  .sv-sync__dot { display: none; }

  // The phone's switch is the band under the filters: after the gear and Clear filters (the default
  // order is 0), on a row of its own (it is 100% wide).
  .sv-controls .sv-view-switch--phone { order: 5; }
}
</style>
